import {
  parsePhoneNumberFromString
} from "libphonenumber-js";
import z from "zod";
import {
  buildPersonName,
  checkIsNotEmpty,
  isNotNullNotUndefined
} from "../helpers/utilities.js";
import { CustomFieldType } from "./customField.js";
import {
  recordingIdSchema,
  RecordingSpeakerRoleType
} from "./recording.js";
const gradingIdSchema = z.uuid().brand();
function asGradingId(id) {
  return gradingIdSchema.parse(id);
}
const transcriptSearchMatchSchema = z.object({
  recording_id: recordingIdSchema,
  /** The full matched chunk. */
  chunk_text: z.string(),
  /** The chunk's chronological position within its recording. */
  chunk_index: z.number(),
  /**
   * The match's relevance rank for the search query. 0 = best match, 1 = second
   * best, etc. Matches are returned chronologically, so a consumer wanting
   * relevance order sorts ascending by this.
   */
  relevance_ranking: z.number(),
  /**
   * Whole-word query-token occurrences within `chunk_text`, as offset ranges.
   * Empty for semantic-only matches (no query word appears verbatim).
   */
  chunk_highlights: z.array(z.object({ start: z.number(), end: z.number() })).readonly(),
  /** Display window over the chunk: excerpt text + highlight ranges. */
  snippet: z.object({
    text: z.string(),
    highlights: z.array(z.object({ start: z.number(), end: z.number() })).readonly()
  })
});
function resolveNextActionTimestamp(nextAction) {
  return nextAction.type === "paused" || nextAction.type === "monitoring_paused" ? nextAction.expires_at : nextAction.scheduled_at;
}
const globalAverageScorePerCriteriaSchema = z.object({
  criteria_node_id: z.string(),
  /** Average of teammate average scores. (Population mean.) */
  global_average_score: z.number(),
  global_average_score_sparkline: z.array(z.number().nullable()).optional(),
  /** Standard deviation of teammate average scores. (Population standard deviation.) */
  average_score_stddev: z.number(),
  /** Median of teammate average scores. */
  global_median_score: z.number().nullable().optional()
});
function getGradingDisplayNames(grading) {
  const firstContact = grading.contacts.at(0);
  const mostRecentRecording = grading.last_recording;
  return buildTeammateAccountGradingDisplayNames({
    firstContact,
    mostRecentRecordingTitle: mostRecentRecording?.title,
    mostRecentRecordingSpeakers: mostRecentRecording?.speakers ?? void 0
  });
}
function buildTeammateAccountGradingDisplayNames(values) {
  const {
    firstContact,
    mostRecentRecordingTitle,
    mostRecentRecordingSpeakers
  } = values;
  const nameFromContact = buildPersonName(firstContact);
  const email = firstContact?.email;
  const phoneNumberFromContact = firstContact?.phone_number;
  const extId = firstContact?.ext_id;
  const phoneNumberFromCustomFields = firstContact ? findPhoneNumberInCustomFields(
    firstContact.custom_fields
  )?.formatInternational() : void 0;
  const phoneNumber = phoneNumberFromContact ?? phoneNumberFromCustomFields;
  const firstCustomerSpeaker = mostRecentRecordingSpeakers?.find(
    (speaker) => speaker.role_type === RecordingSpeakerRoleType.Customer
  );
  const nameFromTranscript = buildPersonName(firstCustomerSpeaker);
  const displayNames = [
    nameFromContact || nameFromTranscript,
    email,
    phoneNumber,
    mostRecentRecordingTitle,
    extId
  ].filter(isNotNullNotUndefined).filter(checkIsNotEmpty);
  const primaryDisplayName = displayNames.shift();
  const secondaryDisplayName = displayNames.shift();
  const isGeneratedFromTranscript = !nameFromContact && !!nameFromTranscript;
  return {
    primaryDisplayName,
    secondaryDisplayName,
    isGeneratedFromTranscript
  };
}
const DISPLAY_NAME_PHONE_CUSTOM_FIELD_NAME = "phone_number";
function findPhoneNumberInCustomFields(customFields) {
  for (const customField of customFields) {
    if (customField.field_name === DISPLAY_NAME_PHONE_CUSTOM_FIELD_NAME && customField.field_type === CustomFieldType.Text && typeof customField.data.value === "string") {
      const phoneNumber = parsePhoneNumberFromString(customField.data.value, {
        defaultCountry: "US",
        // Require the whole thing to be a phone number
        extract: false
      });
      if (phoneNumber) {
        return phoneNumber;
      }
    }
  }
  return void 0;
}
function computeRawRankingScore(metrics) {
  const aucWeight = computeCriteriaOutcomeAucWeight(metrics.auc);
  const lift = metrics.net_revenue ?? metrics.net_conversions;
  return metrics.grading_rate * lift * aucWeight;
}
function computeCriteriaOutcomeAucWeight(auc) {
  return Math.sqrt(Math.max(0, (auc - 0.5) / 0.5));
}
function computeCriteriaOutcomeImpactScore(auc) {
  const aucFactor = (auc - 0.5) / 0.25;
  const impactScore = Math.round(100 * Math.max(0, Math.min(1, aucFactor)));
  return impactScore;
}
function computeCriteriaOutcomeImpactLevel(impactScore) {
  if (impactScore >= 80) {
    return "very_high";
  }
  if (impactScore >= 60) {
    return "high";
  }
  if (impactScore >= 40) {
    return "medium";
  }
  if (impactScore >= 20) {
    return "low";
  }
  return "very_low";
}
const CRITERIA_OUTCOME_IMPACT_SCORE_LEVEL_LABELS = {
  very_high: "Very high",
  high: "High",
  medium: "Medium",
  low: "Low",
  very_low: "Very low"
};
function getCriteriaOutcomeImpactLabel(auc) {
  const impactScore = computeCriteriaOutcomeImpactScore(auc);
  const impactLevel = computeCriteriaOutcomeImpactLevel(impactScore);
  return CRITERIA_OUTCOME_IMPACT_SCORE_LEVEL_LABELS[impactLevel];
}
const HIGH_CORRELATION_THRESHOLD = 0.2;
const MAX_HIGH_IMPACT_CRITERIA_RATIO = 0.25;
const MIN_HIGH_IMPACT_CONVERSIONS = 5;
function computeHighImpactCriteriaIds(correlations, totalCriteriaCount, getNetConversions) {
  if (!correlations || correlations.length === 0 || totalCriteriaCount === 0) {
    return /* @__PURE__ */ new Set();
  }
  const correlationsWithScores = correlations.map((c) => ({
    criteriaNodeId: c.criteriaNodeId,
    rankingScore: computeRawRankingScore(c.metrics)
  }));
  const eligibleCriteria = correlationsWithScores.filter(
    (c) => Math.abs(c.rankingScore) >= HIGH_CORRELATION_THRESHOLD
  );
  const sortedCriteria = [...eligibleCriteria].sort(
    (a, b) => Math.abs(b.rankingScore) - Math.abs(a.rankingScore)
  );
  const maxHighImpact = Math.max(
    1,
    Math.round(totalCriteriaCount * MAX_HIGH_IMPACT_CRITERIA_RATIO)
  );
  let selectedCriteria = sortedCriteria.slice(0, maxHighImpact);
  if (getNetConversions) {
    selectedCriteria = selectedCriteria.filter(
      (c) => getNetConversions(c.criteriaNodeId) >= MIN_HIGH_IMPACT_CONVERSIONS
    );
  }
  return new Set(selectedCriteria.map((c) => c.criteriaNodeId));
}
function isNaOutcomeKey(outcomeKey) {
  return outcomeKey.endsWith(":not_applicable");
}
const CALLS_BASE = 1e3;
const EXPECTED_RELATIVE_IMPROVEMENT = 0.2;
const EXPECTED_RELATIVE_IMPROVEMENT_REVENUE = 0.35;
const MIN_CALIBRATION_MULTIPLIER = 0.5;
const MAX_CALIBRATION_MULTIPLIER = 6;
const MIN_TOP_CRITERIA_COVERAGE = 0.8;
function computeCriteriaConversionsCalibrationMultiplier(currentGreenRate, uncalibratedTopCriteriaTotal, currentAvgRevenuePerOpp) {
  if (uncalibratedTopCriteriaTotal <= 0) {
    return 1;
  }
  if (currentAvgRevenuePerOpp !== void 0 && currentAvgRevenuePerOpp > 0) {
    const targetExtraRevenue = currentAvgRevenuePerOpp * EXPECTED_RELATIVE_IMPROVEMENT_REVENUE * CALLS_BASE;
    const multiplier2 = targetExtraRevenue / uncalibratedTopCriteriaTotal;
    return Math.max(
      MIN_CALIBRATION_MULTIPLIER,
      Math.min(MAX_CALIBRATION_MULTIPLIER, multiplier2)
    );
  }
  if (currentGreenRate === void 0 || currentGreenRate <= 0) {
    return 1;
  }
  const targetExtraConversions = currentGreenRate * EXPECTED_RELATIVE_IMPROVEMENT * CALLS_BASE;
  const multiplier = targetExtraConversions / uncalibratedTopCriteriaTotal;
  return Math.max(
    MIN_CALIBRATION_MULTIPLIER,
    Math.min(MAX_CALIBRATION_MULTIPLIER, multiplier)
  );
}
function computeTopCriteriaTotalForCoverage(scoreMap, targetCoverage) {
  const allScores = Array.from(scoreMap.values()).filter((score) => score > 0);
  const totalScore = allScores.reduce((sum, score) => sum + score, 0);
  if (totalScore <= 0) {
    return 0;
  }
  const sortedScores = allScores.toSorted((a, b) => b - a);
  let cumulativeSum = 0;
  const targetSum = totalScore * targetCoverage;
  for (const score of sortedScores) {
    cumulativeSum += score;
    if (cumulativeSum >= targetSum) {
      break;
    }
  }
  return cumulativeSum;
}
function computeEstimatedCriteriaOutcomeConversions(correlations, totalCriteriaCount, currentGreenRate, options) {
  const scoreMap = /* @__PURE__ */ new Map();
  if (correlations) {
    for (const correlation of correlations) {
      const score = computeRawRankingScore(correlation.metrics);
      scoreMap.set(correlation.criteriaNodeId, score);
    }
  }
  const uncalibratedTopCriteriaTotal = computeTopCriteriaTotalForCoverage(
    scoreMap,
    MIN_TOP_CRITERIA_COVERAGE
  );
  const currentAvgRevenuePerOpp = options?.currentAvgRevenuePerOpp;
  const calibrationMultiplier = computeCriteriaConversionsCalibrationMultiplier(
    currentGreenRate,
    uncalibratedTopCriteriaTotal,
    currentAvgRevenuePerOpp
  );
  const getNetConversions = (criteriaNodeId) => {
    const uncalibratedScore = scoreMap.get(criteriaNodeId) ?? 0;
    return uncalibratedScore * calibrationMultiplier;
  };
  const highImpactCriteriaIds = computeHighImpactCriteriaIds(
    correlations ? [...correlations] : null,
    totalCriteriaCount,
    getNetConversions
  );
  const sortedByScore = (correlations ?? []).map((item) => ({
    criteriaNodeId: item.criteriaNodeId,
    calibratedScore: getNetConversions(item.criteriaNodeId)
  })).sort((a, b) => b.calibratedScore - a.calibratedScore);
  const criteriaRankingMap = /* @__PURE__ */ new Map();
  sortedByScore.forEach((item, index) => {
    criteriaRankingMap.set(item.criteriaNodeId, index + 1);
  });
  return {
    getNetConversions,
    highImpactCriteriaIds,
    criteriaRankingMap
  };
}
function computeEstimatedTeammateCriteriaOutcomeConversions(criteriaOutcomeCorrelation, teammateCriteriaImpact, totalCriteriaCount, currentGreenRate, options) {
  const teammateCorrelations = criteriaOutcomeCorrelation.map((item) => {
    const teammateImpact = teammateCriteriaImpact?.[item.criteriaNodeId];
    if (!teammateImpact) {
      return null;
    }
    return {
      ...item,
      metrics: {
        ...item.metrics,
        net_conversions: teammateImpact.net_conversions,
        // For revenue outcomes, prefer the teammate-specific revenue lift and
        // avoid falling back to the team-level value.
        net_revenue: teammateImpact.net_revenue
      }
    };
  }).filter(isNotNullNotUndefined);
  return computeEstimatedCriteriaOutcomeConversions(
    teammateCorrelations,
    totalCriteriaCount,
    currentGreenRate,
    options
  );
}
const gradingsByTeamSortFieldSchema = z.union([
  z.literal("recording_count"),
  z.literal("playbook_score"),
  z.templateLiteral([z.literal("playbook_section_score:"), z.uuid()]),
  z.templateLiteral([z.literal("playbook_criterion_score:"), z.uuid()]),
  z.templateLiteral([z.literal("computed_field:"), z.uuid()]),
  z.literal("outcome_green_rate"),
  z.literal("outcome_green_count"),
  z.literal("seconds_per_green_outcome"),
  z.literal("outcome_average_revenue"),
  z.literal("revenue_per_hour"),
  z.literal("knowledge_base_adherence_correct_call_rate"),
  z.literal("customer_experience_score"),
  z.literal("talk_ratio")
]);
const gradingsByTeammateSortFieldSchema = z.union([
  gradingsByTeamSortFieldSchema,
  z.literal("last_recorded_at"),
  /**
   * Coaching-goal standing, bucketed by the Goal-progress pill: Goal met >
   * Near goal > On track > No data > no goal > Behind (see
   * `COACHING_GOAL_PILL_SORT_RANKS`); ties within a bucket fall to the
   * default tiebreakers. Teammate rollup only — groups have no per-teammate
   * goal to aggregate. Only meaningful when the request also opts into
   * coaching goal progress; without it every row sorts equal.
   */
  z.literal("coaching_goal_progress")
]);
const gradingsSortDirectionSchema = z.union([
  z.literal("asc"),
  z.literal("desc")
]);
const gradingsByTeammateSortSchema = z.object({
  field: gradingsByTeammateSortFieldSchema,
  direction: gradingsSortDirectionSchema
});
const defaultAscendingTeammateSortFields = Object.freeze([
  // Lower is better.
  "seconds_per_green_outcome"
  // Everything else sorts descending by default, because higher is better.
]);
function getDefaultDirectionForTeammateSortField(sortField) {
  return defaultAscendingTeammateSortFields.includes(sortField) ? "asc" : "desc";
}
const gradingsByGroupSortFieldSchema = z.union([
  gradingsByTeamSortFieldSchema,
  z.literal("group_name")
]);
const gradingsByGroupSortSchema = z.object({
  field: gradingsByGroupSortFieldSchema,
  direction: gradingsSortDirectionSchema
});
const defaultAscendingGroupSortFields = Object.freeze([
  // Alphabetical.
  "group_name",
  // Lower is better.
  "seconds_per_green_outcome"
  // Everything else sorts descending by default, because higher is better.
]);
function getDefaultDirectionForGroupSortField(sortField) {
  return defaultAscendingGroupSortFields.includes(sortField) ? "asc" : "desc";
}
export {
  CRITERIA_OUTCOME_IMPACT_SCORE_LEVEL_LABELS,
  DISPLAY_NAME_PHONE_CUSTOM_FIELD_NAME,
  HIGH_CORRELATION_THRESHOLD,
  asGradingId,
  buildTeammateAccountGradingDisplayNames,
  computeCriteriaOutcomeAucWeight,
  computeCriteriaOutcomeImpactLevel,
  computeCriteriaOutcomeImpactScore,
  computeEstimatedCriteriaOutcomeConversions,
  computeEstimatedTeammateCriteriaOutcomeConversions,
  computeHighImpactCriteriaIds,
  computeRawRankingScore,
  findPhoneNumberInCustomFields,
  getCriteriaOutcomeImpactLabel,
  getDefaultDirectionForGroupSortField,
  getDefaultDirectionForTeammateSortField,
  getGradingDisplayNames,
  globalAverageScorePerCriteriaSchema,
  gradingIdSchema,
  gradingsByGroupSortFieldSchema,
  gradingsByGroupSortSchema,
  gradingsByTeamSortFieldSchema,
  gradingsByTeammateSortFieldSchema,
  gradingsByTeammateSortSchema,
  gradingsSortDirectionSchema,
  isNaOutcomeKey,
  resolveNextActionTimestamp,
  transcriptSearchMatchSchema
};
//# sourceMappingURL=teammatePerformanceSummary.js.map
